import React, { useContext } from 'react';
import * as Styles from './ActivitiesChart.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
} from 'chart.js';
import { format } from 'date-fns';
import { ThemeContext } from 'styled-components';
import { useViewport } from '@/hooks/useViewport';
import { IconCalendar, Skeleton } from 'gatorcomponents';
import emptyIconPath from '../../media/empty-state-graphic-bar.svg';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
);

const ActivitiesChart = ({
  testId = 'activities-chart',
  content,
  data,
  loading = false,
}) => {
  const theme = useContext(ThemeContext);
  const viewport = useViewport();

  const isEmpty = !data || data.length === 0;

  const mdBreakpoint = parseInt(theme.v3.breakpoints.md, 10);
  const isBelowMd = viewport.width < mdBreakpoint;

  const barWidth = isBelowMd ? 0.5 : 0.3;
  const categoryWidth = isBelowMd ? 0.5 : 0.2;

  const labels = data ? data.map(item => format(new Date(`${item.date} 00:00:00`), 'dd/MM')) : [];
  const compromisedFilesData = data ? data.map(item => item.compromised || 0) : [];
  const maliciousCodesData = data ? data.map(item => item.malicious || 0) : [];

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: content.compromisedFilesLabel,
        backgroundColor: theme.v3.colors.brand.snappyBlue[90],
        data: compromisedFilesData,
        borderRadius: 4,
        categoryPercentage: categoryWidth,
        barPercentage: barWidth,
        stack: 'Stack 0',
      },
      {
        type: 'bar',
        label: content.maliciousCodesLabel,
        backgroundColor: theme.v3.colors.brand.snappyBlue[80],
        data: maliciousCodesData,
        borderRadius: 4,
        categoryPercentage: categoryWidth,
        barPercentage: barWidth,
        stack: 'Stack 0',
      },
    ],
  };

  const chartOptions = {
    backgroundColor: 'transparent',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 25,
          color: theme.v3.colors.neutral[40],
          font: {
            family: theme.v3.font.family.brand,
            size: theme.v3.font.sizes.small,
            weight: theme.v3.font.weights.medium,
          },
          usePointStyle: true,
          boxHeight: 9,
        },
      },
      tooltip: {
        bodyFont: {
          family: theme.v3.font.family.brand,
        },
        titleFont: {
          family: theme.v3.font.family.brand,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.v3.colors.neutral[40],
          font: {
            family: theme.v3.font.family.brand,
            size: theme.v3.font.sizes.small,
            weight: theme.v3.font.weights.medium,
          },
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          color: theme.v3.colors.neutral[90],
          lineWidth: 1,
        },
        ticks: {
          color: theme.v3.colors.neutral[40],
          font: {
            family: theme.v3.font.family.brand,
            size: theme.v3.font.sizes.small,
            weight: theme.v3.font.weights.medium,
          },
        },
      },
    },
  };

  return (
    <Styles.Wrapper data-testid={testId}>
      <Styles.Header>
        <Styles.TitleAdapter>
          <Styles.Title>{content.title}</Styles.Title>
        </Styles.TitleAdapter>
        <Styles.InfoDate>
          <IconCalendar color="lowLight" size="sm" />
          {content.filter.last30Days}
        </Styles.InfoDate>
      </Styles.Header>

      {loading && (
        <Skeleton height="400px" width="100%" />
      )}
      {(!loading && !isEmpty) && (
        <Styles.ChartContainer>
          <Chart type="bar" data={chartData} options={chartOptions} />
        </Styles.ChartContainer>
      )}
      {(!loading && isEmpty) && (
        <Styles.EmptyStateContainer data-testid="empty-state">
          <Styles.EmptyImage src={emptyIconPath} alt={content.emptyImageAlt} />
          <Styles.EmptyTitle>{content.emptyTitle}</Styles.EmptyTitle>
          <Styles.EmptyDescription>{content.emptyDescription}</Styles.EmptyDescription>
        </Styles.EmptyStateContainer>
      )}
    </Styles.Wrapper>
  );
};

export default withErrorBoundary(ActivitiesChart, 'ActivitiesChart');
